import { useState, useMemo } from 'react';
import { Layout } from '../../components/Layout';
import { get, post, patch, del } from '../../api/superAdminClient';
import useSWR from 'swr';
import { Pagination } from '../../components/Pagination';
import { FilterBar, FilterItem } from '../../components/FilterBar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { TenantDetailModal } from './TenantDetailModal';
import { Modal } from '../../components/Modal';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { taipeiToUtc } from '@pos/shared/utils/time';

/**
 * 租戶列表回應類型
 */
interface TenantListResponse {
  success: boolean;
  data: {
    tenants: Array<{
      id: string;
      name: string;
      slug: string;
      phone: string | null;
      isActive: boolean;
      planId: number | null;
      planName: string | null;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * 方案列表回應類型
 */
interface PlanListResponse {
  success: boolean;
  data: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

/**
 * Tenants 頁面
 */
export function Tenants() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('');
  const [planIdFilter, setPlanIdFilter] = useState<number | undefined>(
    undefined
  );
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 公告設定相關狀態
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<
    Array<{
      id: number;
      content: string;
      startsAt: string;
      endsAt: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>
  >([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState<{
    id?: number;
    content: string;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
  } | null>(null);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);

  // 退訂分析相關狀態
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState<{
    totalTenants: number;
    planStats: {
      pro: number;
      basic: number;
      free: number;
    };
    cancelledTenants: number;
    reasonStats: Array<{ reason: string; count: number }>;
    missingFeatureCount: number;
    otherReasonCount: number;
  } | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // 取得方案列表（用於篩選）
  const { data: plansData } = useSWR<PlanListResponse>(
    '/api/super-admin/plans',
    get as any
  );

  // 構建查詢參數
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', '20');
    if (search) {
      params.set('search', search);
    }
    if (isActiveFilter) {
      params.set('isActive', isActiveFilter === 'true' ? 'true' : 'false');
    }
    if (planIdFilter !== undefined) {
      params.set('planId', planIdFilter.toString());
    }
    return params.toString();
  }, [page, search, isActiveFilter, planIdFilter]);

  // 取得租戶列表
  const { data, error, isLoading, mutate } = useSWR<TenantListResponse>(
    `/api/super-admin/tenants?${queryParams}`,
    get as any
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1); // 重置到第一頁
  };

  const handleFilterChange = () => {
    setPage(1); // 重置到第一頁
  };

  const handleClearFilters = () => {
    setSearch('');
    setIsActiveFilter('');
    setPlanIdFilter(undefined);
    setPage(1);
  };

  const handleViewDetail = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedTenantId(null);
  };

  const hasActiveFilters = !!(
    search ||
    isActiveFilter ||
    planIdFilter !== undefined
  );

  // 載入公告列表
  const loadAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const res = await get<{ success: boolean; data: typeof announcements }>(
        '/api/super-admin/tenants/announcements'
      );
      setAnnouncements(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '載入失敗';
      toast.error(msg);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  // 開啟公告設定 modal
  const handleOpenAnnouncementModal = async () => {
    setAnnouncementModalOpen(true);
    await loadAnnouncements();
  };

  // 新增公告
  const handleCreateAnnouncement = () => {
    // 使用當前台北時間
    const now = new Date();
    const taipeiNow = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Taipei' })
    );
    const weekLater = new Date(taipeiNow.getTime() + 7 * 24 * 60 * 60 * 1000);

    setEditingAnnouncement({
      content: '',
      startsAt: format(taipeiNow, "yyyy-MM-dd'T'HH:mm"),
      endsAt: format(weekLater, "yyyy-MM-dd'T'HH:mm"),
      isActive: true,
    });
  };

  // 編輯公告
  const handleEditAnnouncement = (announcement: (typeof announcements)[0]) => {
    // 後端已回傳台北時間，直接使用（格式：YYYY-MM-DDTHH:mm:ss+08:00）
    setEditingAnnouncement({
      id: announcement.id,
      content: announcement.content,
      startsAt: announcement.startsAt.slice(0, 16), // YYYY-MM-DDTHH:mm
      endsAt: announcement.endsAt.slice(0, 16), // YYYY-MM-DDTHH:mm
      isActive: announcement.isActive,
    });
  };

  // 儲存公告
  const handleSaveAnnouncement = async () => {
    if (!editingAnnouncement) return;
    setSavingAnnouncement(true);
    try {
      // 將用戶輸入的台北時間轉換為 UTC
      const startsAtUtc = taipeiToUtc(editingAnnouncement.startsAt);
      const endsAtUtc = taipeiToUtc(editingAnnouncement.endsAt);

      if (editingAnnouncement.id) {
        // 更新
        await patch(
          `/api/super-admin/tenants/announcements/${editingAnnouncement.id}`,
          {
            content: editingAnnouncement.content,
            startsAt: startsAtUtc,
            endsAt: endsAtUtc,
            isActive: editingAnnouncement.isActive,
          }
        );
        toast.success('公告已更新');
      } else {
        // 新增
        await post('/api/super-admin/tenants/announcements', {
          content: editingAnnouncement.content,
          startsAt: startsAtUtc,
          endsAt: endsAtUtc,
          isActive: editingAnnouncement.isActive,
        });
        toast.success('公告已建立');
      }
      setEditingAnnouncement(null);
      await loadAnnouncements();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '儲存失敗';
      toast.error(msg);
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // 刪除公告
  const handleDeleteAnnouncement = async (id: number) => {
    if (!confirm('確定要刪除此公告嗎？')) return;
    try {
      await del(`/api/super-admin/tenants/announcements/${id}`);
      toast.success('公告已刪除');
      await loadAnnouncements();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '刪除失敗';
      toast.error(msg);
    }
  };

  // 載入退訂分析
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await get<{ success: boolean; data: typeof analytics }>(
        '/api/super-admin/tenants/cancellation-analytics'
      );
      setAnalytics(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '載入失敗';
      toast.error(msg);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // 開啟退訂分析 modal
  const handleOpenAnalyticsModal = async () => {
    setAnalyticsModalOpen(true);
    await loadAnalytics();
  };

  // 退訂理由中文對照
  const reasonLabels: Record<string, string> = {
    cost_over_budget: '超出預算',
    low_value: '價值不符',
    low_usage: '使用頻率低',
    business_ended: '業務結束',
    missing_feature: '缺少功能',
    too_complex: '操作複雜',
    unstable: '系統不穩定',
    slow_support: '客服回應慢',
    switched_competitor: '改用競爭對手',
    no_longer_needed: '不再需要',
    taking_break: '暫時休息',
    other: '其他',
  };

  return (
    <Layout title="租戶管理">
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleOpenAnnouncementModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            公告設定
          </button>
          <button
            type="button"
            onClick={handleOpenAnalyticsModal}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            退訂分析
          </button>
        </div>
        {/* 搜尋和篩選 */}
        <div className="space-y-4">
          {/* 搜尋欄 */}
          <div className="bg-white rounded-lg shadow p-4">
            <input
              type="text"
              placeholder="搜尋租戶名稱或 Slug..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* 篩選器 */}
          <FilterBar
            onClear={handleClearFilters}
            showClearButton={hasActiveFilters}
          >
            <FilterItem label="狀態">
              <select
                value={isActiveFilter}
                onChange={e => {
                  setIsActiveFilter(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                <option value="true">啟用</option>
                <option value="false">停用</option>
              </select>
            </FilterItem>
            <FilterItem label="方案">
              <select
                value={
                  planIdFilter === undefined ? '' : planIdFilter.toString()
                }
                onChange={e => {
                  const value = e.target.value;
                  setPlanIdFilter(
                    value === '' ? undefined : parseInt(value, 10)
                  );
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">全部</option>
                {plansData?.data?.map(plan => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </FilterItem>
          </FilterBar>
        </div>

        {/* 租戶列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="p-8">
              <ErrorDisplay error={error} onRetry={() => mutate()} />
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      名稱
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      方案
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      建立時間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.data?.tenants?.map(tenant => (
                    <tr key={tenant.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tenant.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tenant.slug}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            tenant.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {tenant.isActive ? '啟用' : '停用'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tenant.planName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(tenant.createdAt), 'yyyy-MM-dd', {
                          locale: zhTW,
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewDetail(tenant.id)}
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          詳情
                        </button>
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-4 text-center text-gray-500"
                      >
                        尚無資料
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* 分頁 */}
              {data?.data && data.data.totalPages > 1 && (
                <div className="px-6 py-4 border-t">
                  <Pagination
                    currentPage={data.data.page}
                    totalPages={data.data.totalPages}
                    total={data.data.total}
                    limit={data.data.limit}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* 租戶詳情 Modal */}
        <TenantDetailModal
          tenantId={selectedTenantId}
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetail}
          onUpdate={() => {
            mutate();
            handleCloseDetail();
          }}
        />

        {/* 公告設定 Modal */}
        <Modal
          isOpen={announcementModalOpen}
          onClose={() => {
            setAnnouncementModalOpen(false);
            setEditingAnnouncement(null);
          }}
          title="公告設定"
          footer={
            <>
              {editingAnnouncement ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEditingAnnouncement(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAnnouncement}
                    disabled={
                      savingAnnouncement || !editingAnnouncement.content.trim()
                    }
                    className="px-4 py-2 text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingAnnouncement ? '儲存中…' : '儲存'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setAnnouncementModalOpen(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  關閉
                </button>
              )}
            </>
          }
        >
          {editingAnnouncement ? (
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="announcement-content"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  公告內容
                </label>
                <textarea
                  id="announcement-content"
                  value={editingAnnouncement.content}
                  onChange={e =>
                    setEditingAnnouncement({
                      ...editingAnnouncement,
                      content: e.target.value,
                    })
                  }
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="輸入公告內容..."
                />
              </div>
              <div>
                <label
                  htmlFor="announcement-starts-at"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  開始時間
                </label>
                <input
                  id="announcement-starts-at"
                  type="datetime-local"
                  value={editingAnnouncement.startsAt}
                  onChange={e =>
                    setEditingAnnouncement({
                      ...editingAnnouncement,
                      startsAt: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label
                  htmlFor="announcement-ends-at"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  結束時間
                </label>
                <input
                  id="announcement-ends-at"
                  type="datetime-local"
                  value={editingAnnouncement.endsAt}
                  onChange={e =>
                    setEditingAnnouncement({
                      ...editingAnnouncement,
                      endsAt: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingAnnouncement.isActive}
                    onChange={e =>
                      setEditingAnnouncement({
                        ...editingAnnouncement,
                        isActive: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">啟用</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCreateAnnouncement}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  新增公告
                </button>
              </div>
              {loadingAnnouncements ? (
                <div className="text-center py-8">
                  <LoadingSpinner />
                </div>
              ) : announcements.length === 0 ? (
                <p className="text-center text-gray-500 py-8">尚無公告</p>
              ) : (
                <div className="space-y-3">
                  {announcements.map(announcement => (
                    <div
                      key={announcement.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">
                            {format(
                              new Date(announcement.startsAt),
                              'yyyy-MM-dd HH:mm',
                              { locale: zhTW }
                            )}{' '}
                            至{' '}
                            {format(
                              new Date(announcement.endsAt),
                              'yyyy-MM-dd HH:mm',
                              { locale: zhTW }
                            )}
                          </p>
                          <p className="text-gray-900 whitespace-pre-wrap">
                            {announcement.content}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              announcement.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {announcement.isActive ? '啟用' : '停用'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={() => handleEditAnnouncement(announcement)}
                          className="px-3 py-1 text-sm text-primary hover:text-primary/80"
                        >
                          編輯
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteAnnouncement(announcement.id)
                          }
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                        >
                          刪除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* 退訂分析 Modal */}
        <Modal
          isOpen={analyticsModalOpen}
          onClose={() => setAnalyticsModalOpen(false)}
          title="退訂分析"
          footer={
            <button
              type="button"
              onClick={() => setAnalyticsModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              關閉
            </button>
          }
        >
          {loadingAnalytics ? (
            <div className="text-center py-8">
              <LoadingSpinner />
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">總租戶</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {analytics.totalTenants}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">退訂</p>
                  <p className="text-2xl font-bold text-red-600">
                    {analytics.cancelledTenants}
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  方案統計
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">專業</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {analytics.planStats.pro}
                    </p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">一般</p>
                    <p className="text-2xl font-bold text-indigo-600">
                      {analytics.planStats.basic}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">免費</p>
                    <p className="text-2xl font-bold text-gray-600">
                      {analytics.planStats.free}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  退訂理由統計
                </h3>
                <div className="space-y-2">
                  {analytics.reasonStats
                    .filter(stat => stat.count > 0)
                    .map(stat => (
                      <div
                        key={stat.reason}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-700">
                          {reasonLabels[stat.reason] || stat.reason}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {stat.count}
                        </span>
                      </div>
                    ))}
                  {analytics.missingFeatureCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        缺少功能（詳細說明）
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {analytics.missingFeatureCount}
                      </span>
                    </div>
                  )}
                  {analytics.otherReasonCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        其他原因（詳細說明）
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {analytics.otherReasonCount}
                      </span>
                    </div>
                  )}
                  {analytics.reasonStats.every(stat => stat.count === 0) &&
                    analytics.missingFeatureCount === 0 &&
                    analytics.otherReasonCount === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        尚無退訂資料
                      </p>
                    )}
                </div>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </Layout>
  );
}
